
import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Database, TrendingUp, TrendingDown, BarChart2, PackageCheck } from "lucide-react";

const InventoryInsightsTab = () => {
  const insightsData = [
    {
      title: "Stock Trends",
      icon: TrendingUp,
      color: "bg-blue-100 text-blue-700",
      insights: [
        "Dairy products are showing 15% higher turnover than last month",
        "Frozen foods category has slowed by 8% in the last 2 weeks",
        "Beverages are consistently your top-moving category"
      ]
    },
    {
      title: "Risk Alerts",
      icon: TrendingDown,
      color: "bg-red-100 text-red-700",
      insights: [
        "5 products are at risk of stockout within 3 days",
        "12 items are approaching expiry dates in the next week",
        "Seasonal items (back-to-school) should be ordered this week"
      ]
    },
    {
      title: "Optimization Opportunities",
      icon: BarChart2,
      color: "bg-green-100 text-green-700",
      insights: [
        "You're overstocking cleaning supplies by 35% - consider reducing",
        "Current rice inventory could last 45 days - longer than optimal",
        "Order frequency for dairy could be increased to reduce waste"
      ]
    },
    {
      title: "Supplier Insights",
      icon: PackageCheck,
      color: "bg-purple-100 text-purple-700",
      insights: [
        "EcoFarms' lead time has improved by 20% in last quarter",
        "Dairy Fresh has 3x more product returns than other suppliers",
        "Consider consolidating orders from smaller suppliers"
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Inventory Insights (AI-powered)</h2>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 sm:p-6 rounded-lg border border-blue-100">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="p-4 rounded-full bg-blue-100">
            <Database className="h-8 w-8 text-blue-600" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h3 className="text-lg font-semibold text-blue-800">AI-Powered Inventory Analysis</h3>
            <p className="text-sm text-blue-600 mt-1">
              Our AI has analyzed your inventory data and found several optimization opportunities.
              Insights are updated daily based on your latest sales and stock information.
            </p>
          </div>
          <Button className="bg-blue-700 hover:bg-blue-800">
            Run Custom Analysis
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {insightsData.map((insight, index) => (
          <Card key={index} className="border-l-4" style={{ borderLeftColor: insight.color.split(' ')[1] }}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <div className={`p-2 rounded-md ${insight.color}`}>
                  <insight.icon className="h-5 w-5" />
                </div>
                {insight.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {insight.insights.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-amber-100 rounded-full">
            <BarChart2 className="h-6 w-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-amber-800">Recommendation of the Day</h3>
            <p className="text-sm text-amber-700">
              Consider running a promotion on yogurt products. You currently have 15% higher stock than optimal,
              and historical data shows these items will approach expiration in 2 weeks.
            </p>
          </div>
          <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100">
            Create Promotion
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InventoryInsightsTab;
